import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Loader2, Send, X, Paperclip, Megaphone, Plus, ImagePlus, CheckCircle2 } from "lucide-react";
import { MemberPicker } from "./MemberPicker";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";

// Phase 4 (2026-02): PM/founder/partner may switch to "Announcement" (post_type="announce",
// badged NJM + pinnable, may carry a PDF/image attachment) — enforced server-side too
// (community.announce scope, kernel/permissions.py); every other community.write role never
// sees this toggle at all and can only ever create a normal "work" post.
const ANNOUNCE_ROLES = ["founder", "partner", "pm"];
const MAX_FILES = 6;
const FEEDBACK_PROMPT_MAX = 300;
const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// Nav Cleanup, Stage 2B (2026-11): same client-side-convenience-only extraction as the
// server's `_extract_youtube_id` (modules/community.py) — never calls the YouTube API, and
// never the final gate (the server always re-validates). Purely so the composer can show a
// thumbnail + "Video found" before the user submits.
const YT_ID_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const YT_NON_VIDEO_RE = /youtube\.com\/(?:channel\/|playlist\?|\/@|c\/|user\/)/i;
const extractYoutubeId = (url) => {
  if (!url || YT_NON_VIDEO_RE.test(url)) return null;
  const m = url.match(YT_ID_RE);
  return m ? m[1] : null;
};

// Inviting first-post UX (2026-02): `expanded`/`onExpandedChange` are lifted into
// CommunityTab so its warm empty-state CTA can open this composer directly (nothing else
// about the form changed) — same collapsed-pill / full-form toggle as before, just controlled
// from the parent instead of purely local state.
export function PostComposer({ members, onPosted, expanded, onExpandedChange, pictureModeRequested = false, onBusyChange }) {
  const { user } = useAuth();
  const canAnnounce = ANNOUNCE_ROLES.includes(user?.role);
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [tagged, setTagged] = useState([]);
  const [postType, setPostType] = useState("work");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [progress, setProgress] = useState(null);
  const sending = useRef(false);
  const retry = useRef(null);
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;
  useEffect(() => { busyCallback.current?.(submitting || uncertain); }, [submitting, uncertain]);
  useEffect(() => () => { busyCallback.current?.(false); }, []);
  useEffect(() => {
    if (!submitting && !uncertain) return undefined;
    const preventUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [submitting, uncertain]);
  const [showPicker, setShowPicker] = useState(false);
  const guard = useEmailGuard();
  const fileInputRef = useRef(null);
  const [pictureMode, setPictureMode] = useState(false);
  // Read-only capability probe (GET /v1/community/composer-capabilities) so picture mode can say
  // up front when uploads aren't connected; null = unknown (server still decides on submit).
  const [caps, setCaps] = useState(null);
  useEffect(() => {
    api.get("/v1/community/composer-capabilities").then((r) => setCaps(r.data)).catch(() => setCaps(null));
  }, []);

  // Request Feedback fields (Nav Cleanup, Stage 2B, 2026-11) — `linkUrl` above doubles as the
  // YouTube link field here (the two never show together, `buildForm` routes it to the right
  // form key below), everything else is new local state.
  const [fbTitle, setFbTitle] = useState("");
  const [feedbackPrompt, setFeedbackPrompt] = useState("");
  const [feedbackScope, setFeedbackScope] = useState("EVERYONE");
  const [allowedRoles, setAllowedRoles] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [videoCheck, setVideoCheck] = useState(null); // { valid, videoId } | null (untouched)

  useEffect(() => {
    // Same canonical list "All roles" uses, via the existing role-list endpoint
    // (GET /v1/build/roles) — same precedent as CreateNjmCollaborationModal.jsx's role picker.
    api.get("/v1/build/roles").then((r) => setRoleOptions(r.data.items || [])).catch(() => {});
  }, []);

  const cancel = (force = false) => {
    if ((sending.current || uncertain) && force !== true) return;
    retry.current = null; setUncertain(false); setProgress(null);
    busyCallback.current?.(false);
    setText(""); setLinkUrl(""); setTagged([]); setFiles([]); setPostType("work");
    setFbTitle(""); setFeedbackPrompt(""); setFeedbackScope("EVERYONE"); setAllowedRoles([]); setVideoCheck(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setPictureMode(false);
    onExpandedChange(false);
  };

  const onPickFiles = (picked) => {
    const incoming = Array.from(picked || []);
    if (!incoming.length) return;
    const next = [...files, ...incoming].slice(0, MAX_FILES);
    if (files.length + incoming.length > MAX_FILES) toast.error(`Up to ${MAX_FILES} images per post`);
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const toggleRole = (r) => setAllowedRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const onVideoUrlBlur = () => {
    const trimmed = linkUrl.trim();
    if (!trimmed) { setVideoCheck(null); return; }
    setVideoCheck({ valid: !!extractYoutubeId(trimmed), videoId: extractYoutubeId(trimmed) });
  };

  const canSubmitFeedback = Boolean(videoCheck?.valid) && Boolean(feedbackPrompt.trim()) && (feedbackScope === "EVERYONE" || allowedRoles.length > 0);

  const buildForm = (extra = {}) => {
    const form = new FormData();
    form.append("text", text);
    form.append("tagged_contributor_ids", JSON.stringify(tagged.map((t) => t.id)));
    form.append("post_type", postType);
    if (postType === "feedback_request") {
      form.append("title", fbTitle);
      form.append("video_url", linkUrl);
      form.append("feedback_prompt", feedbackPrompt);
      form.append("feedback_scope", feedbackScope);
      form.append("allowed_roles", JSON.stringify(feedbackScope === "SELECTED_ROLES" ? allowedRoles : []));
    } else if (linkUrl) {
      form.append("link_url", linkUrl);
    }
    if (extra.confirmation_token) form.append("confirmation_token", extra.confirmation_token);
    if (extra.confirmed_count != null) form.append("confirmed_count", String(extra.confirmed_count));
    if (retry.current) form.append("request_id", retry.current.key);
    files.forEach((f) => form.append("files", f));
    return form;
  };

  // Email Send Guard (2026-02): an announcement fans out a real email to every eligible
  // freelancer's real inbox — the exact incident-risk this guard exists for. The FIRST post
  // call here returns `confirmation_required` instead of posting when there's anyone eligible
  // to email at all — nothing is created until the modal's "Send" is confirmed.
  const submit = async () => {
    if (postType === "feedback_request") {
      if (!canSubmitFeedback) return;
    } else if (!text.trim()) {
      toast.error("Write something first"); return;
    }
    if (sending.current) return;
    if (!window.crypto?.getRandomValues) { toast.error("Posting needs a secure browser connection. Please reopen the HTTPS app link."); return; }
    sending.current = true;
    setSubmitting(true); setProgress(0); busyCallback.current?.(true);
    const signature = JSON.stringify([text, linkUrl, tagged.map((t) => t.id), postType, fbTitle,
      feedbackPrompt, feedbackScope, allowedRoles, files.map((f) => [f.name, f.size, f.lastModified])]);
    if (!retry.current || (!uncertain && retry.current.signature !== signature)) {
      const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
      retry.current = { signature, key: [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("") };
    }
    const options = { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      onUploadProgress: (event) => setProgress(event.total ? Math.min(100, Math.round(event.loaded * 100 / event.total)) : null) };
    try {
      const r = await api.post("/v1/community/posts", buildForm(), options);
      if (guard.intercept(r.data, async (typedCount) => {
        sending.current = true; setSubmitting(true); busyCallback.current?.(true);
        try {
          await api.post("/v1/community/posts", buildForm({ confirmation_token: r.data.confirmation_token, confirmed_count: typedCount }), options);
          toast.success("Announcement posted"); cancel(true); onPosted();
        } catch (error) {
          const pending = !error.response || ["post_in_progress", "post_status_uncertain", "post_needs_review"].includes(error.response?.data?.detail?.code);
          setUncertain(pending); busyCallback.current?.(pending);
          throw error;
        } finally { sending.current = false; setSubmitting(false); }
      })) return;
      toast.success(postType === "announce" ? "Announcement posted" : postType === "feedback_request" ? "Feedback request posted" : "Posted to the community");
      cancel(true);
      onPosted();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const pending = !e.response || ["post_in_progress", "post_status_uncertain", "post_needs_review"].includes(code);
      setUncertain(pending); busyCallback.current?.(pending);
      if (pending) toast.error("Your post may still be saving. Keep this draft open and retry to check it safely.");
      else if (code === "drive_upload_failed" || code === "post_save_failed") toast.error("Your post wasn't saved. Keep your pictures here and try again.");
      else if (code === "image_too_many_pixels") toast.error("This photo is too large to process. Use a smaller copy (up to 36 megapixels).");
      else if (code === "post_request_changed") toast.error("This retry doesn't match the original post. Contact NJM before trying a new post.");
      else if (code === "drive_not_configured" || code === "drive_folder_not_configured") toast.error("Attachments aren't connected yet — check back soon");
      else if (code === "invalid_file_type" || code === "invalid_image_file") toast.error("Use a JPG, PNG, WEBP, or PDF");
      else if (code === "cannot_mix_pdf_and_images") toast.error("Attach either a single PDF, or one or more images — not both");
      else if (code === "too_many_files") toast.error(`Up to ${MAX_FILES} images per post`);
      else if (code === "file_too_large") toast.error("A file is too large");
      else if (code === "confirmation_required") toast.error("That confirmation expired — try posting again");
      else if (code === "invalid_youtube_url") toast.error("Use a YouTube link (youtube.com or youtu.be).");
      else if (code === "empty_feedback_prompt") toast.error("Add what feedback you'd like");
      else if (code === "invalid_feedback_scope" || code === "feedback_roles_required") toast.error("Pick at least one role, or choose Everyone");
      else if (code === "invalid_role") toast.error("One of the selected roles isn't valid anymore — refresh and try again");
      else toast.error("Couldn't submit your post");
    } finally {
      sending.current = false; setSubmitting(false);
    }
  };

  const handlePostClick = () => submit();

  // Design pass (2026-02): the composer opens COLLAPSED (a one-line pill matching the
  // reference mockup) and expands into the full form below on click/focus — same submit
  // handler, same fields, same multipart payload as before; this is purely a progressive-
  // disclosure UI wrapper, nothing about what gets sent to the backend changed.
  // Batch polish (2026-08): Bavna (PM) couldn't find the Announcement toggle at all — it only
  // ever appeared AFTER expanding the collapsed pill, with zero hint from the collapsed state
  // that it existed. This jumps straight into the full form with postType pre-set, so
  // founder/partner/pm see the option before they've even clicked "New post".
  const openAsAnnouncement = () => { setPostType("announce"); onExpandedChange(true); };

  // Floor "Create" FAB (2026-10 staging UI pass): when the parent opens this composer from the
  // floating menu it sets `pictureModeRequested`, so the composer leads with the picture
  // affordance (or the explicit "uploads aren't connected" notice). Text posts still work.
  useEffect(() => {
    if (expanded && pictureModeRequested) { setPostType("work"); setPictureMode(true); }
  }, [expanded, pictureModeRequested]);

  if (!expanded) {
    // Phones: no inline row — the floating Create button is the single posting entry.
    // Desktop/tablet: the original composer row, unchanged.
    return (
      <div className="hidden sm:flex items-center gap-3 flex-wrap" data-testid="community-post-composer-collapsed">
        <div className="h-9 w-9 rounded-full bg-ink text-brand flex items-center justify-center text-xs font-bold shrink-0">{initials(user?.name)}</div>
        <button onClick={() => onExpandedChange(true)} data-testid="community-post-composer-expand"
          className="flex-1 min-w-[160px] text-left rounded-full border border-hairline bg-paper px-4 py-2.5 text-sm text-faint hover:border-brand/50 transition-colors">
          Share a BTS shot, recent work, or a tip for the crew…
        </button>
        {canAnnounce && (
          <button onClick={openAsAnnouncement} data-testid="community-post-composer-announce-shortcut"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand/40 px-4 py-2.5 text-xs font-semibold text-brand-dim hover:bg-brand/10 transition-colors shrink-0">
            <Megaphone className="h-3.5 w-3.5" /> Post announcement
          </button>
        )}
        <button onClick={() => onExpandedChange(true)} data-testid="community-post-composer-new-post-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-4 py-2.5 text-xs font-mont font-bold text-paper hover:bg-ink/85 transition-colors shrink-0">
          <Plus className="h-3.5 w-3.5" /> New post
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-hairline bg-surface2/30 p-4 sm:p-5 space-y-3" data-testid="community-post-composer">
      {(submitting || uncertain) && <div role="status" aria-live="polite" className="text-sm text-ink-soft" data-testid="community-post-progress">{submitting ? progress === 100 ? "Pictures sent. Saving your post…" : `Sending${progress == null ? "…" : ` ${progress}%…`}` : "Check your post by retrying. Keep this draft open until its status is confirmed."}</div>}
      <fieldset disabled={submitting || uncertain} className="space-y-3 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="community-post-type-toggle">
        <button onClick={() => setPostType("work")} data-testid="community-post-type-work"
          className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${postType === "work" ? "bg-ink text-paper" : "border border-hairline text-ink-soft"}`}>
          Work Post
        </button>
        {canAnnounce && (
          <button onClick={() => setPostType("announce")} data-testid="community-post-type-announce"
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${postType === "announce" ? "bg-brand text-ink" : "border border-hairline text-ink-soft"}`}>
            <Megaphone className="h-3 w-3" /> Announcement
          </button>
        )}
        <button onClick={() => setPostType("feedback_request")} data-testid="community-post-type-feedback_request"
          className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${postType === "feedback_request" ? "bg-brand text-ink" : "border border-hairline text-ink-soft"}`}>
          Request Feedback
        </button>
      </div>

      {pictureMode && postType === "work" && files.length === 0 && (
        caps && !caps.attachments_available ? (
          <div role="status" className="rounded-lg border border-brand/40 bg-[#FFFCF2] px-3 py-2.5 text-xs text-ink-soft" data-testid="composer-pictures-unavailable">
            Picture uploads aren't connected in this app yet, so photos can't be attached right now. You can still share a text post below.
          </div>
        ) : (
          <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="composer-choose-pictures-btn"
            className="flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg border-[1.5px] border-dashed border-brand bg-[#FFFCF2] px-4 py-3 text-sm font-mont font-bold text-ink hover:bg-brand/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2">
            <ImagePlus aria-hidden="true" className="h-4 w-4" /> Choose pictures
          </button>
        )
      )}

      {postType === "feedback_request" ? (
        <div className="space-y-3" data-testid="community-feedback-request-form">
          <div>
            <label className="text-xs font-medium text-ink-soft">Title <span className="text-faint font-normal">(optional)</span></label>
            <input value={fbTitle} onChange={(e) => setFbTitle(e.target.value)} placeholder="Give it a name"
              data-testid="community-feedback-title-input"
              className="mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-base md:text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand/50" />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">YouTube link</label>
            <input
              value={linkUrl} onChange={(e) => { setLinkUrl(e.target.value); setVideoCheck(null); }} onBlur={onVideoUrlBlur}
              placeholder="youtube.com/watch?v=… or youtu.be/…"
              data-testid="community-feedback-video-input"
              className={`mt-1 w-full rounded-md border bg-paper px-3 py-2 text-base md:text-sm text-ink placeholder:text-faint focus:outline-none ${videoCheck && !videoCheck.valid ? "border-red-400" : "border-hairline focus:border-brand/50"}`}
            />
            {videoCheck?.valid ? (
              <div className="mt-1.5 flex items-center gap-2 text-xs text-ink-soft" data-testid="community-feedback-video-found">
                <img src={`https://i.ytimg.com/vi/${videoCheck.videoId}/default.jpg`} alt="" className="h-7 w-10 rounded object-cover" />
                <CheckCircle2 className="h-3.5 w-3.5 text-brand-dim" /> Video found
              </div>
            ) : videoCheck && !videoCheck.valid ? (
              <p className="mt-1 text-xs text-red-500" data-testid="community-feedback-video-error">Use a YouTube link (youtube.com or youtu.be).</p>
            ) : (
              <p className="mt-1 text-xs text-faint">Public or unlisted videos both work.</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">What is it and where is it at?</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="What is it and where is it at?"
              data-testid="community-feedback-description-input"
              className="mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-base md:text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand/50 resize-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">What feedback do you want? <span className="text-red-500">*</span></label>
            <textarea
              value={feedbackPrompt} onChange={(e) => setFeedbackPrompt(e.target.value.slice(0, FEEDBACK_PROMPT_MAX))} rows={3}
              placeholder="e.g. Pacing of the first 30 seconds, colour grade, sound mix"
              data-testid="community-feedback-prompt-input"
              className="mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-base md:text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand/50 resize-none"
            />
            <div className="text-right text-[11px] text-faint mt-0.5">{feedbackPrompt.length} / {FEEDBACK_PROMPT_MAX}</div>
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">Who can give feedback</label>
            <div className="flex items-center gap-1.5 mt-1.5">
              <button type="button" onClick={() => setFeedbackScope("EVERYONE")} data-testid="community-feedback-scope-everyone"
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${feedbackScope === "EVERYONE" ? "bg-brand text-ink" : "border border-hairline text-ink-soft"}`}>
                Everyone
              </button>
              <button type="button" onClick={() => setFeedbackScope("SELECTED_ROLES")} data-testid="community-feedback-scope-selected"
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${feedbackScope === "SELECTED_ROLES" ? "bg-brand text-ink" : "border border-hairline text-ink-soft"}`}>
                Selected roles
              </button>
            </div>
            {feedbackScope === "SELECTED_ROLES" && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="community-feedback-role-chips">
                {roleOptions.map((r) => (
                  <button key={r} type="button" onClick={() => toggleRole(r)} data-testid={`community-feedback-role-chip-${r}`}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${allowedRoles.includes(r) ? "bg-brand text-ink" : "border border-hairline text-ink-soft"}`}>
                    {allowedRoles.includes(r) ? "✓ " : ""}{r}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <textarea
            autoFocus
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder={postType === "announce" ? "Write an announcement for everyone…" : "Share a BTS shot, recent work, or a tip for the crew…"} rows={3}
            data-testid="community-post-text-input"
            className="w-full rounded-md border border-hairline bg-paper px-3 py-2 text-base md:text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand/50 resize-none"
          />
          <input
            value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Optional link — YouTube, Vimeo, or a website"
            data-testid="community-post-link-input"
            className="w-full rounded-md border border-hairline bg-paper px-3 py-2 text-xs text-ink placeholder:text-faint focus:outline-none focus:border-brand/50"
          />
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="community-post-file-chips">
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-hairline px-2 py-1 text-[11px] text-ink-soft" data-testid={`community-post-file-chip-${i}`}>
                  <Paperclip className="h-3 w-3" /> {f.name}
                  <button onClick={() => setFiles(files.filter((_, x) => x !== i))} data-testid={`community-post-file-remove-${i}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </>
      )}
      {tagged.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="community-tagged-chips">
          {tagged.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 rounded-full bg-hairline px-2 py-1 text-[11px] text-ink-soft">
              {t.name}
              <button onClick={() => setTagged(tagged.filter((x) => x.id !== t.id))} data-testid={`community-untag-${t.id}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      </fieldset>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <fieldset disabled={submitting || uncertain} className="flex items-center gap-3">
          {postType !== "feedback_request" ? (
            <>
              <button onClick={() => setShowPicker(true)} data-testid="community-tag-contributor-btn" className="min-h-[44px] min-w-[44px] px-2 text-xs text-ink-soft hover:text-ink transition-colors">
                + Tag contributors
              </button>
              <button onClick={() => fileInputRef.current?.click()} data-testid="community-post-attach-btn"
                disabled={caps?.attachments_available === false}
                title={caps?.attachments_available === false ? "Picture uploads aren't connected in this app yet" : undefined}
                className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <ImagePlus className="h-3.5 w-3.5" /> Add photos
              </button>
            </>
          ) : !canSubmitFeedback ? (
            <span className="text-[11px] text-faint" data-testid="community-feedback-submit-hint">Add a YouTube link and what you'd like feedback on.</span>
          ) : null}
          <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => onPickFiles(e.target.files)} className="hidden" data-testid="community-post-file-input" />
        </fieldset>
        <div className="flex items-center gap-2">
          <button disabled={submitting || uncertain} onClick={cancel} data-testid="community-post-composer-cancel" className="min-h-[44px] min-w-[44px] px-2 text-xs text-ink-soft hover:text-ink transition-colors">
            Cancel
          </button>
          <button onClick={handlePostClick} disabled={submitting || (postType === "feedback_request" && !canSubmitFeedback)} data-testid="community-post-submit-btn"
            className="min-h-[44px] inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink hover:bg-brand/90 transition-colors disabled:opacity-50">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {uncertain ? "Check / retry post" : postType === "feedback_request" ? "Post request" : "Post"}
          </button>
        </div>
      </div>
      {showPicker && !submitting && !uncertain && <MemberPicker members={members} selected={tagged} onChange={setTagged} onClose={() => setShowPicker(false)} />}
      <EmailConfirmModal open={guard.open} preview={guard.preview} busy={guard.busy} onConfirm={guard.confirm} onCancel={guard.cancel} />
    </div>
  );
}

